import i18next from 'i18next'
import { ModelsMoneyFormat } from '@itsrever/dashboard-api'
import { Platforms } from './types'
export * from './types'
export * from './device'

export function equalJSON(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b)
}

export const isValidEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
  return re.test(String(email).toLowerCase())
}

export const isValidPhone = (checkphone: string): boolean => {
  const re = /^[+]?[^9][0-9()-]{6,20}$/i
  const phone = checkphone.replace(/ /g, '')
  return re.test(phone)
}

export const isValidIdentifier = (identifier: string): boolean => {
  const re = /^[a-zA-Z0-9_ -]{1,20}$/
  return re.test(identifier)
}

export function deepCopy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * "DE, GB" -> "Alemania, Reino Unido". The API stores ISO codes because they
 * are data; the merchant reads the names in their own language.
 */
export function formatCountries(countries: string, lang: string): string {
  const names = new Intl.DisplayNames([lang], { type: 'region' })
  return countries
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
    .map((code) =>
      /^[A-Za-z]{2}$/.test(code) ? (names.of(code.toUpperCase()) ?? code) : code
    )
    .join(', ')
}

// Without a language it formats in the user's, as analytics does.
export function formatCurrency(
  amount: string,
  currency: string,
  options?: {
    lang?: string
  } & Intl.NumberFormatOptions
) {
  return Intl.NumberFormat(options?.lang ?? i18next.language ?? 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    ...options
  }).format(Number(amount))
}

export function checkImage(url: string) {
  const http = new XMLHttpRequest()

  http.open('HEAD', url, false)
  http.send()

  return http.status != 404
}

export function isReturnRateAnalyticsAllowed(platform?: string) {
  return (
    platform === Platforms.Shopify ||
    platform === Platforms.MagentoV2 ||
    platform === Platforms.Prestashop ||
    platform === Platforms.Salesforce
  )
}

export function downloadCSVFile(content: string, fileName: string) {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(new Blob([content]))
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function promiseTimer(seconds: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true)
    }, seconds * 1000)
  })
}

export const getDateFromDate = (date: Date, lng: string): string => {
  return date.toLocaleDateString(lng, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export const getLongDateFromDate = (date: Date, lng: string): string => {
  return date.toLocaleDateString(lng, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const getLongDate = (seconds: number, lng: string): string => {
  const date = new Date(seconds * 1000)
  return date.toLocaleDateString(lng, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const formatPrice = (
  price: number,
  moneyFormat: ModelsMoneyFormat,
  multiply = true
): string => {
  let ints = ''
  let decimals = ''
  let indexSeparator = undefined
  if (multiply && moneyFormat.amount_multiplied_by) {
    const realPrice = price / moneyFormat.amount_multiplied_by
    if (`${realPrice}`.includes('.')) {
      indexSeparator = `${realPrice}`.indexOf('.')
    }
    if (indexSeparator) {
      ints = `${realPrice}`.substring(0, indexSeparator)
      decimals = `${realPrice}`.substring(
        indexSeparator + 1,
        `${realPrice}`.length
      )
    } else {
      ints = `${realPrice}`
    }
  } else {
    ints = `${Math.trunc(price)}`
    decimals = `${price % 1}`
  }
  let addSign = false
  if (ints[0] === '-') {
    addSign = true
    ints = ints.substring(1, ints.length)
  }
  ints = ints.split('').reverse().join('')
  ints = ints.match(/.{1,3}/g)?.join(moneyFormat.thousand_separator ?? '') ?? ''
  ints = ints.split('').reverse().join('')
  if (addSign) ints = '-' + ints
  const places = moneyFormat.visible_number_of_decimals
  if (places !== undefined) {
    decimals = decimals.substring(0, places).padEnd(places, '0')
  }
  if (moneyFormat.decimal_separator) {
    // Zero-decimal currencies (JPY, CLP…) have no fraction, so no separator either.
    const value = decimals
      ? ints + moneyFormat.decimal_separator + decimals
      : ints
    if (moneyFormat.is_currency_left_position) {
      return `${moneyFormat.currency_symbol} ${value}`
    }
    return `${value} ${moneyFormat.currency_symbol}`
  }
  if (moneyFormat.is_currency_left_position) {
    return `${moneyFormat.currency_symbol}${price}`
  }
  return `${price}${moneyFormat.currency_symbol ?? ''}`
}

// Some barcode scanners prepend a GS1 Application Identifier in parentheses (e.g. "(Y)", "(00)") when scanning barcode information into the search field.
const BARCODE_SCANNER_PREFIX = /^\([^)]*\)/

export const stripBarcodeScannerPrefix = (value: string): string =>
  value.replace(BARCODE_SCANNER_PREFIX, '')

export function toRGB(hex: string) {
  const bigint = parseInt(hex.replace('#', ''), 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `rgb(${r}, ${g}, ${b}, 0.1)`
}
